import { useEffect, useState } from "react";
import { UseCustomerBalanceV2 } from "../server/fetcher";
import useBankAccounts from "./useBankAccounts";
import { BLOCK_STATUS, resolveBlockStatus, getBlockWarning } from "../functions/customerBlock";

// How long to wait for /customer/balance before letting the user through anyway.
// fetcher.js catches every error and resolves to undefined, so SWR's `error` never fires
// and a failing request looks identical to one still in flight. Without this bound a
// fail-closed guard would spin forever on a flaky connection.
const CHECK_TIMEOUT_MS = 8000;

/**
 * Whether this owner is blocked for non-payment, plus the copy for the warning banner.
 *
 * Always asks for mainOnly=1: on /customer/balancev2 that argument controls whether the
 * API discloses billing FIGURES, not whether it answers. (On the old v1 endpoint the
 * default of 0 made it reply a bare `false`, which is what left the central app's page
 * guards permanently inert.)
 *
 * Both SWR keys are already fetched by the Drawer, so mounting this on many pages costs
 * no extra requests.
 */
export default function useCustomerBlock(owner_id, { mainOnly = 1 } = {}) {
  const { data: customer } = UseCustomerBalanceV2(owner_id, mainOnly);
  // Via useBankAccounts so the blocked screen gets the v3 payload and shows each
  // account under its own holder. Going straight to a versioned fetcher here is what
  // left this path on the deprecated shape.
  const { data: bankInformation } = useBankAccounts(owner_id);
  const [timedOut, setTimedOut] = useState(false);

  // navigator.onLine is read directly rather than through useNetwork(): that hook polls
  // an external host on a 3s interval, which is too much to mount on every guarded page.
  const isOnline = typeof navigator === "undefined" ? true : navigator.onLine !== false;

  useEffect(() => {
    if (!owner_id || customer !== undefined) return undefined;

    const timer = setTimeout(() => setTimedOut(true), CHECK_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [owner_id, customer]);

  const status = resolveBlockStatus({ ownerId: owner_id, customer, isOnline, timedOut });

  return {
    status,
    isChecking: status === BLOCK_STATUS.LOADING,
    isBlocked: status === BLOCK_STATUS.BLOCKED,
    customer,
    bankInformation,
    warning: getBlockWarning(customer),
    // Spread straight into <RenderDisabledCustomer {...block.disabledProps} />. Bundled so
    // that giving the blocked screen more context later does not mean editing every
    // guarded page again.
    disabledProps: { bankInformation, customer_id: customer?.customer_id },
  };
}
